import { Fn }			from "ts-base/fn";
import { Numbers }		from "ts-base/numbers";
import { Equal }		from "ts-base/equal";
import { Comparator }	from "ts-base/comparator";

import { DateTime }		from "ts-base/time/dateTime";
import { IsoDate }		from "@v3/data/time/isoDate";

export type Gregorian	= Readonly<{
	// 1..31
	day:	number,
	// 1..12
	month:	number,
	// 100..
	year:	number,
}>;

export namespace Gregorian {
	export const fromIsoDate	= (iso:IsoDate):Gregorian	=> {
		const parts	= iso.split(/[^\d]+/);
		if (parts.length !== 3)	throw new Error(`unexpected iso date ${iso}`);
		const [ year, month, day ] = parts.map(it => parseInt(it));
		return { day, month, year };
	};

	// yyyy-mm-dd
	export const toIsoDate	= (it:Gregorian):IsoDate	=>
		IsoDate.brand(
			[
				Numbers.intDigits(it.year,	4),
				Numbers.intDigits(it.month,	2),
				Numbers.intDigits(it.day,	2),
			]
			.join("-")
		);

	//-----------------------------------------------------------------------------

	export const ofDateTime	= (it:DateTime):Gregorian	=> ({
		year:	it.year,
		month:	it.month,
		day:	it.day,
	});

	export const padToDateTime	= (it:Gregorian):DateTime	=> ({
		year:	it.year,
		month:	it.month,
		day:	it.day,
		hour:	0,
		minute:	0,
		second:	0,
		milli:	0,
	});

	//-----------------------------------------------------------------------------

	// NOTE converting from Date to Gregorian reduces precision from millisecond to day

	export type BuiltinZone	= {
		fromDate:	Fn<Date, Gregorian>,
		toDate:		Fn<Gregorian, Date>,
	};

	export const utc:BuiltinZone	= {
		fromDate:	(it:Date):Gregorian	=> ofDateTime(DateTime.utc.fromDate(it)),
		toDate:		(it:Gregorian):Date	=> DateTime.utc.toDate(padToDateTime(it)),
	};

	export const local:BuiltinZone	= {
		fromDate:	(it:Date):Gregorian	=> ofDateTime(DateTime.local.fromDate(it)),
		toDate:		(it:Gregorian):Date	=> DateTime.local.toDate(padToDateTime(it)),
	};

	//-----------------------------------------------------------------------------


	export const equal:Equal<Gregorian>	= (a, b) =>
		a.year	=== b.year	&&
		a.month	=== b.month	&&
		a.day	=== b.day;

	export const compare:Comparator<Gregorian> =
		Comparator.joinN(
			Comparator.contraMap(Numbers.compare)((it:Gregorian) => it.year),
			Comparator.contraMap(Numbers.compare)((it:Gregorian) => it.month),
			Comparator.contraMap(Numbers.compare)((it:Gregorian) => it.day),
		);
}
